import React from 'react';
import { useRecordContext, useTranslate } from 'react-admin';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { LastSettlementRow } from './LastSettlementRow';
import type { LastOctopusSettlement } from './types';
import { Contactless } from '@mui/icons-material';

export type ReaderState = 'ready' | 'connecting' | 'error' | 'stopped';

export interface ReaderHealth {
  state: ReaderState;
  device_id: string | null;
  log_ok: boolean;
  tat_number: string | null;
}

export interface KioskTimeZone {
  offsetMinutes: number;
  ok: boolean;
}

const STATE_COLOR: Record<ReaderState, 'success' | 'warning' | 'error' | 'default'> = { ready: 'success', connecting: 'warning', error: 'error', stopped: 'default' };

export function formatUtcOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const magnitude = Math.abs(minutes);
  const hours = String(Math.floor(magnitude / 60)).padStart(2, '0');
  const rest = String(magnitude % 60).padStart(2, '0');
  return `UTC${sign}${hours}:${rest}`;
}

export function readerOf(health: unknown): ReaderHealth | null {
  if (health === null || typeof health !== 'object') return null;
  const reader = (health as Record<string, unknown>).reader;
  if (reader === null || reader === undefined || typeof reader !== 'object') return null;
  return reader as ReaderHealth;
}

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 0.75, borderBottom: '1px solid #F0F0F0', flexWrap: 'wrap' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Box sx={{ textAlign: 'right' }}>{children}</Box>
  </Box>
);

export const OctopusReaderPanel = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !('configuredTatNumber' in record)) return null;
  const t = (key: string, options?: Record<string, unknown>) => translate(`resources.kiosk-devices.readerPanel.${key}`, options);
  const reader = readerOf(record.health);
  const timeZone = record.timeZone as KioskTimeZone | null;
  const configured = record.configuredTatNumber as string;
  const tatMismatch = reader !== null && (configured === '' || reader.tat_number !== configured);
  const tatColor = tatMismatch ? 'error' : 'text.primary';

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Contactless sx={{ mr: 2, color: '#00C853' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>{t('title')}</Typography>
        </Box>
        {reader === null ? (
          <Typography variant="body2" color="text.secondary" data-testid="reader-no-report">{t('noReport')}</Typography>
        ) : (
          <>
            <Row label={t('state')}>
              <Chip size="small" data-testid="reader-state" color={STATE_COLOR[reader.state]} label={translate(`resources.kiosk-devices.update.readerStates.${reader.state}`)} />
            </Row>
            <Row label={t('deviceNumber')}>
              <Typography variant="body2">{reader.device_id === null ? '-' : reader.device_id}</Typography>
            </Row>
            <Row label={t('tat')}>
              <Typography variant="body2" color={tatColor} data-testid="tat-reported" data-mismatch={String(tatMismatch)}>
                {t('tatReported', { value: reader.tat_number === null ? t('tatNotReported') : reader.tat_number })}
              </Typography>
              <Typography variant="body2" color={tatColor} data-testid="tat-configured" data-mismatch={String(tatMismatch)}>
                {t('tatConfigured', { value: configured === '' ? t('tatNotSet') : configured })}
              </Typography>
            </Row>
            <Row label={t('logUpload')}>
              <Chip size="small" data-testid="reader-log" color={reader.log_ok ? 'success' : 'error'} label={t(reader.log_ok ? 'logOk' : 'logFailing')} />
            </Row>
          </>
        )}
        <Row label={t('timeZone')}>
          {timeZone === null ? (
            <Typography variant="body2" color="text.secondary" data-testid="time-zone">{t('timeZoneNotReported')}</Typography>
          ) : (
            <Chip
              size="small"
              data-testid="time-zone"
              color={timeZone.ok ? 'success' : 'error'}
              label={t(timeZone.ok ? 'timeZoneOk' : 'timeZoneWrong', { offset: formatUtcOffset(timeZone.offsetMinutes) })}
            />
          )}
        </Row>
        <LastSettlementRow settlement={record.lastOctopusSettlement as LastOctopusSettlement | null} kioskId={String(record.id)} />
      </CardContent>
    </Card>
  );
};
